import React, { useState, useCallback, useRef, useEffect } from 'react';
import { usePostHog } from 'posthog-js/react';
import { useTranslation } from 'react-i18next';

// components
import ClickableInput from 'eCoach/ClickableInput';
import Chatbar from 'eCoach/chatbar/Chatbar';

// hooks
import { usePosthogCapture } from 'eCoach/posthogCapture/formFilling';
import { useFormTemplates } from 'shared/hooks/api/formFilling';

// styles
import * as StyledChatbar from '../_styles/Chatbar.style';
import { UserInputType } from '../utils';
import * as StyledPromptControls from '../_styles/PromptControls.style';

interface TemplateChoice {
  id: string;
  displayName: string;
}

interface FormFillingTemplateSelectionProps {
  sendUserInput: (
    text: string,
    type: number,
    extraData: Record<string, string> | undefined
  ) => void;
  inputType: number;
  message: string;
  choices?: TemplateChoice[];
}

const WARNING_LETTER_OPTION_VALUE = 'warning_letter';

export default function FormFillingTemplateSelection({
  sendUserInput,
  choices,
}: FormFillingTemplateSelectionProps): JSX.Element {
  const { t } = useTranslation('pageEcoach');
  const posthog = usePostHog();
  const formFilling = usePosthogCapture();

  const { data: templatesData } = useFormTemplates(!!choices);
  const templates =
    choices ||
    templatesData.map((template) => ({
      id: template.id,
      displayName: template.document_translation.title,
    }));
  const [selectedTemplate, setSelectedTemplate] =
    useState<TemplateChoice | null>(null);
  const [selectedWarningLetter, setSelectedWarningLetter] =
    useState<boolean>(false);
  const [selectionKey, setSelectionKey] = useState<number>(0);
  const chatbarRef = useRef<HTMLDivElement>(null);

  const canAccessWarningLetter = posthog.isFeatureEnabled('warning_letter');

  // Scroll to chatbar when a template or warning letter is selected (to focus user on submit)
  useEffect(() => {
    if (!selectedTemplate && !selectedWarningLetter) return;
    requestAnimationFrame(() => {
      chatbarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
  }, [selectedTemplate, selectedWarningLetter]);

  const handleSubmit = useCallback(
    (input: string) => {
      const trimmed = input.trim();

      // Check if warning letter was selected
      const warningLetterText = t(
        'chatStart.ChatLanding.Warning Letters - Attendance'
      );
      if (
        selectedWarningLetter ||
        trimmed.toLowerCase() === warningLetterText.toLowerCase()
      ) {
        sendUserInput(
          t('chatStart.I need help generating a warning letter.'),
          UserInputType.WARNING_LETTER_GENERATION_START,
          undefined
        );
        formFilling.dropdownSelectedTemplate({ form_name: 'Warning Letter' });
        return;
      }

      // Try to resolve the submitted text to one of the available templates by title
      const matchedTemplate =
        templates?.find(
          (tmpl) =>
            tmpl.displayName.trim().toLowerCase() === trimmed.toLowerCase()
        ) || null;

      if (matchedTemplate) {
        sendUserInput(
          matchedTemplate.displayName,
          UserInputType.FORM_TEMPLATE_CHOICE,
          { form_template_id: matchedTemplate.id }
        );
        formFilling.dropdownSelectedTemplate({
          form_name: matchedTemplate.displayName,
        });
        return;
      }

      // If no template matched, treat it as a regular back-and-forth message (escape option)
      sendUserInput(
        trimmed,
        UserInputType.BACK_AND_FORTH_CONVERSATION_INPUT,
        undefined
      );
    },
    [formFilling, sendUserInput, templates, selectedWarningLetter, t]
  );

  const handleSelectChange = useCallback(
    (value: string) => {
      if (value === WARNING_LETTER_OPTION_VALUE) {
        setSelectedWarningLetter(true);
        setSelectedTemplate(null);
      } else {
        const template = templates?.find((tmpl) => tmpl.id === value);
        setSelectedTemplate(template || null);
        setSelectedWarningLetter(false);
      }
      // Increment selection key to force Chatbar to update even if same template is selected
      setSelectionKey((prev) => prev + 1);
    },
    [templates]
  );

  // Build select options: warning letter (if accessible) + form templates
  const selectOptions = [
    ...(canAccessWarningLetter
      ? [
          {
            value: WARNING_LETTER_OPTION_VALUE,
            label: t('chatStart.ChatLanding.Warning Letters - Attendance'),
          },
        ]
      : []),
    ...(templates?.map((template) => ({
      value: template.id,
      label: template.displayName,
    })) || []),
  ];

  const selectedValue = selectedWarningLetter
    ? WARNING_LETTER_OPTION_VALUE
    : selectedTemplate?.id;

  const initialInput = selectedWarningLetter
    ? t('chatStart.ChatLanding.Warning Letters - Attendance')
    : selectedTemplate?.displayName || '';

  return (
    <StyledChatbar.Section>
      <StyledPromptControls.ButtonsColumn>
        <ClickableInput
          inputText={t('FormFilling.Select a form')}
          inputType="select"
          selectOptions={selectOptions}
          selectedValue={selectedValue}
          onSelectChange={handleSelectChange}
        />
      </StyledPromptControls.ButtonsColumn>

      <div ref={chatbarRef} style={{ width: '100%' }}>
        <Chatbar
          key={selectionKey}
          initialInput={initialInput}
          onSubmit={handleSubmit}
        />
      </div>
    </StyledChatbar.Section>
  );
}
